/**
 * Question Coordinator Module
 * Coordinates question rendering and dispatches to appropriate question type renderers
 * Created to break circular dependencies between question modules
 */

import { EVENT_TYPES } from '../../core/event-types.js';
import { logEvent } from '../../core/event-logger.js';
import appState from '../../core/app-state.js';
import { generateOptionLabel, shuffleQuestionOptions, type ShuffleResult } from './question-utils.js';
import { 
  renderSummaryChip, 
  updateQuestionProgressBar, 
  handleAnswerSelection,
  registerRenderCurrentQuestion
} from './question-manager.js';
import { 
  renderPreQuestionsCard, 
  shouldShowPreQuestionsCard,
  registerRenderFirstQuestion 
} from './pre-questions-card.js';
import type { Clip, Question, MultipleChoiceQuestion } from '../../types/clip.js';

// Specialized renderers (registered to avoid circular dependencies)
type RenderSequenceQuestionCallback = (clip: Clip, idx: number) => void;
type RenderClozeQuestionCallback = (clip: Clip, idx: number) => void;

let renderSequenceQuestionCallback: RenderSequenceQuestionCallback | null = null;
let renderClozeQuestionCallback: RenderClozeQuestionCallback | null = null;

/**
 * Specialized renderers interface
 */
interface SpecializedRenderers {
  renderSequenceQuestion: RenderSequenceQuestionCallback;
  renderClozeQuestion: RenderClozeQuestionCallback;
}

/**
 * Register specialized question renderers
 * @param renderers - Object with renderSequenceQuestion and renderClozeQuestion
 */
export function registerSpecializedRenderers(renderers: SpecializedRenderers): void {
  renderSequenceQuestionCallback = renderers.renderSequenceQuestion;
  renderClozeQuestionCallback = renderers.renderClozeQuestion;
}

/**
 * Render the current question for a clip
 * Dispatches to specialized renderers based on question type
 * @param clip - The current clip
 * @param idx - The clip index
 */
export function renderCurrentQuestion(clip: Clip, idx: number): void {
  if (!clip.questions || !clip.questions.length) return;
  
  const questionContainer = document.getElementById('questionContainer');
  if (!questionContainer) return;
  
  // Clear the question container
  questionContainer.innerHTML = '';
  
  // Check if we should show the pre-questions card (first listen gate)
  if (appState.currentQuestionIndex === 0 && shouldShowPreQuestionsCard(clip)) {
    renderPreQuestionsCard(questionContainer);
    return;
  }
  
  // If we've answered all questions, show summary chip
  if (appState.currentQuestionIndex >= clip.questions.length) {
    renderSummaryChip(clip, questionContainer);
    
    // Update progress bar to show completed questions
    updateQuestionProgressBar();
    return;
  }
  
  const q: Question = clip.questions[appState.currentQuestionIndex] as Question;
  
  // Check if this is a sequence question and dispatch to specialized renderer
  if (q.type === 'sequence') {
    if (renderSequenceQuestionCallback) {
      renderSequenceQuestionCallback(clip, idx);
    }
    return;
  }
  
  // Check if this is a cloze question and dispatch to specialized renderer
  if (q.type === 'cloze') {
    if (renderClozeQuestionCallback) {
      renderClozeQuestionCallback(clip, idx);
    }
    return;
  }
  
  // At this point, q is a MultipleChoiceQuestion (not sequence or cloze)
  const mcq = q as MultipleChoiceQuestion;
  
  // Shuffle options to prevent position memorization
  const correctAnswerIndex = 
    (typeof mcq.answerIndex === 'number') 
      ? mcq.answerIndex 
      : 0;
  
  const { shuffledOptions, shuffledAnswerIndex, originalIndices }: ShuffleResult = 
    shuffleQuestionOptions(mcq.options, correctAnswerIndex);
  
  const questionDiv = document.createElement('div');
  questionDiv.className = 'question-block mt-1';
  questionDiv.setAttribute('role', 'group');
  questionDiv.setAttribute('aria-labelledby', `question-prompt-${clip.id}-${appState.currentQuestionIndex}`);
  
  // Clear the central feedback region (kept for backwards compatibility)
  const feedbackRegion = document.getElementById('feedback-region');
  if (feedbackRegion) {
    feedbackRegion.innerHTML = '';
  }

  const questionText = document.createElement('p');
  questionText.textContent = mcq.prompt; // Use prompt instead of question
  questionText.className = 'mb-3 text-lg font-semibold text-gray-800 text-center';
  questionText.id = `question-prompt-${clip.id}-${appState.currentQuestionIndex}`;
  questionDiv.appendChild(questionText);

  const buttons: HTMLButtonElement[] = [];

  shuffledOptions.forEach((opt, idxOpt) => {
    const btn = document.createElement('button');
    // Use new answer-card class for styling
    btn.className = 'answer-card';
    
    // Add appropriate ARIA attributes for better semantics
    btn.setAttribute('role', 'option');
    btn.setAttribute('aria-selected', 'false');
    
    // Check if the button already has a badge (prevent duplicates on re-render)
    if (!btn.querySelector('.answer-badge')) {
      // Generate the option label (A, B, C, etc.)
      const label = generateOptionLabel(idxOpt);
      
      // Create badge element
      const badge = document.createElement('span');
      badge.className = 'answer-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = label;
      
      // Create visually hidden span for screen readers
      const srLabel = document.createElement('span');
      srLabel.className = 'sr-only';
      srLabel.textContent = `${label}. `;
      
      // Create text container
      const textSpan = document.createElement('span');
      textSpan.className = 'answer-text';
      textSpan.textContent = opt;
      
      // Add elements to button
      btn.appendChild(badge);
      btn.appendChild(srLabel);
      btn.appendChild(textSpan);
    } else {
      // If badge exists (shouldn't happen normally), just set text content
      btn.textContent = opt;
    }
    
    // Add event listener for answer selection
    btn.addEventListener('click', (event) => {
      handleAnswerSelection(event, clip, mcq, idxOpt, buttons, questionDiv, shuffledAnswerIndex, originalIndices[idxOpt] ?? idxOpt);
    });
    
    buttons.push(btn);
    questionDiv.appendChild(btn);
  });
  
  // Create feedback container inside question block
  const questionFeedback = document.createElement('div');
  questionFeedback.className = 'question-feedback';
  questionFeedback.setAttribute('aria-live', 'polite');
  questionDiv.appendChild(questionFeedback);
  
  questionContainer.appendChild(questionDiv);
  
  // Store question start time for timing user responses
  appState.questionStartTime = Date.now();
  
  // Log question_render event
  logEvent(EVENT_TYPES['QUESTION_RENDER']!, {
    clipId: clip.id,
    questionIndex: appState.currentQuestionIndex,
    questionText: mcq.prompt,
    optionsCount: mcq.options.length
  });
}

// Register this function with question-manager to avoid circular dependency
registerRenderCurrentQuestion(renderCurrentQuestion);

// Register this function with pre-questions-card for callback
registerRenderFirstQuestion(renderCurrentQuestion);
